import { useState, useImperativeHandle, useCallback } from 'react';
import { lang } from '@umu/ukit-lang';
import { IFormData, IFormErrors, IUseCaetBindEmailParams, IUseCaetBindEmailReturn } from '../interface/bindEmail';
import { handleDefaultAfterReceive } from '../dataFlow';

/**
 * CaetBindEmail 组件逻辑层 Hook
 */
export const useCaetBindEmail = (params: IUseCaetBindEmailParams): IUseCaetBindEmailReturn => {
    const { onClose, onConfirm, onAfterReceive = handleDefaultAfterReceive, isMobile = false, ref } = params;

    // 状态管理
    const [formData, setFormData] = useState<IFormData>({
        certificateName: '',
        email: '',
        confirmEmail: '',
    });

    const [errors, setErrors] = useState<IFormErrors>({});

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
        getValueInfo: () => formData,
        validateForm,
    }));

    /**
     * 处理输入框值变化
     */
    const handleInputChange = useCallback((field: keyof IFormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // 清除对应字段的错误信息
        setErrors((prev) => {
            if (prev[field]) {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            }
            return prev;
        });
    }, []);

    /**
     * 验证邮箱格式
     */
    const validateEmail = useCallback((email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    /**
     * 验证表单
     */
    const validateForm = useCallback((): boolean => {
        const newErrors: IFormErrors = {};

        if (!formData.certificateName?.trim()) {
            newErrors.certificateName = lang('请输入证书展示姓名');
        }

        if (!formData.email?.trim()) {
            newErrors.email = lang('请输入邮箱地址');
        } else if (!validateEmail(formData.email)) {
            newErrors.email = lang('请输入有效的邮箱地址');
        }

        if (!formData.confirmEmail?.trim()) {
            newErrors.confirmEmail = lang('请确认邮箱地址');
        } else if (formData.email !== formData.confirmEmail) {
            newErrors.confirmEmail = lang('两次输入的邮箱地址不一致');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, validateEmail]);

    /**
     * 处理确认按钮点击
     */
    const handleConfirm = useCallback((): void => {
        if (validateForm()) {
            // 校验通过，调用父组件的确认回调
            onConfirm?.();
        }
        // 校验不通过时，不执行任何操作，保持弹窗打开
    }, [validateForm, onConfirm]);
    /**
     * 处理稍后领取按钮点击
     */
    const handleAfterReceive = useCallback((): void => {
        onClose?.();
        onAfterReceive();
    }, [onClose]);
    return {
        formData,
        errors,
        showFooterButton: !isMobile,
        showHeaderButton: isMobile,
        showTooltips: !isMobile,
        preClassName: isMobile ? 'mobile' : 'pc',
        handleInputChange,
        handleConfirm,
        handleAfterReceive,
    };
};
